import { useState, useCallback } from "react";

import Box from "@mui/material/Box";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";

import { useCurrentUserContext } from "../../../../../common/contexts/CurrentUserContext";
import { trpc } from "../../../../../common/helpers/trpc";

import { RecaptchaAction } from "@/common/constants/recaptcha";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { DashboardLink } from "@/dashboard-design-system/Link";

export const VerifyEmailAlert = ({ needToLinkBankAccount }: { needToLinkBankAccount?: boolean }) => {
  const { t } = useTranslate();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useCurrentUserContext();
  const { executeTrpc } = useRecaptchaRetry();

  const { mutateAsync: sendVerifyEmailNotificationMutation } = trpc.sendVerifyEmailNotification.useMutation();
  const [isLoading, setIsLoading] = useState(false);

  const sendVerifyEmailNotification = useCallback(async () => {
    setIsLoading(true);
    const { error } = await executeTrpc(
      RecaptchaAction.SendVerifyEmailNotification,
      currentUser?.email,
      sendVerifyEmailNotificationMutation,
      // Locale-less path: router.push re-adds the confirmation page's locale. Live hash: step changes bypass router.asPath.
      { redirectionUrl: `${router.asPath.split("#")[0]}${window.location.hash}` }
    );
    if (error) {
      enqueueSnackbar(error.message, { vibe: "danger" });
    } else {
      enqueueSnackbar(t("dashboard", "writeEmailModal.emailSent"), { vibe: "positive" });
    }
    setIsLoading(false);
  }, [currentUser?.email, enqueueSnackbar, executeTrpc, router.asPath, sendVerifyEmailNotificationMutation, t]);

  const email = currentUser?.email ?? "";

  return (
    <DashboardInfobox
      vibe="warning"
      size="small"
      type="alert"
      data-test="verify-email-alert"
      buttonsProps={[
        {
          key: "resend-verify-email",
          children: (
            <>
              {needToLinkBankAccount
                ? t("dashboard", "resend")
                : t("dashboard", "writeEmailModal.verifyEmailBanner.cta")}
            </>
          ),
          onClick: sendVerifyEmailNotification,
          isLoading,
          inline: true,
        },
      ]}
    >
      {needToLinkBankAccount ? (
        <>
          {t("dashboard", "writeEmailModal.pleaseValidateYourEmailAndBank")}
          <strong data-sensitive="email">({email})</strong>{" "}
          {t("dashboard", "writeEmailModal.pleaseValidateYourEmail.reason")}{" "}
          {t("dashboard", "writeEmailModal.pleaseValidateYourEmail2")}
        </>
      ) : (
        <>
          <Box component="span" sx={{ fontWeight: 700, display: "block" }}>
            {t("dashboard", "writeEmailModal.verifyEmailBanner.title")}
          </Box>
          <Trans
            space="dashboard"
            i18nKey="writeEmailModal.verifyEmailBanner.body"
            values={{ email }}
            components={{
              emailLink: (
                <DashboardLink href={`mailto:${email}`} vibe="warning" underline="always" data-sensitive="email" />
              ),
            }}
          />
        </>
      )}
    </DashboardInfobox>
  );
};
